<template>
  <section class="ai-visibility-overview-tab">
    <loading-state v-if="loading" label="Loading overview data..."/>
    <error-state
        v-else-if="loadError"
        :message="loadError"
        retry-label="Retry overview"
        @retry="loadOverview"
    />
    <empty-state
        v-else-if="!overview.metrics"
        title="No overview data yet"
        description="Run this AI Visibility tracker to see score, trend, competitor, question, source, and recommendation data."
    />
    <template v-else>
      <metric-grid>
        <score-metric-card
            title="Your AI Visibility Score"
            :value="visibilityScoreMetric"
            :scale-labels="['Starting out', 'Good', 'Excellent']"
            info-title="A single score that summarizes how visible your brand is in AI answers for your category. It combines mentions, position, share of voice, and citations."
            :trend="visibilityScoreTrend"
        />
        <metric-card
            title="How often you're mentioned"
            :value="mentionRateMetic"
            suffix="%"
            description="Mentioned in most AI answers we tracked"
            info-title="How often your brand appears in AI answers for your category. Higher is better."
            :trend="mentionRateTrend"
        />
        <metric-card
            title="Your share of the conversation"
            :value="shareOfConversationMetic"
            suffix="%"
            description="of AI answers include your brand"
            info-title="Your share of all brand mentions across AI answers in this category."
            :trend="shareOfConversationTrend"
        />
        <metric-card
            title="Times AI linked to your site"
            :value="linkedToSiteCountMetic"
            description="direct links in AI answers this week"
            info-title="Some AI answers don’t cite sources. This counts only answers that include citations."
            :trend="linkedToSiteCountTrend"
        />
      </metric-grid>

      <div class="ai-visibility-overview-tab__section-grid">
        <visibility-trend-chart :chart="visibilityScoreHistory"/>
        <competitor-score-panel
            title="Who else AI is talking about"
            subtitle="The brands mentioned most in your space"
            :competitors="overview.competitors"
            :action-to="competitorsTabRoute"
            @view-all="navigateToDetailTab('AiVisibilityCompetitors')"
        />
      </div>

      <div class="ai-visibility-overview-tab__section-grid">
        <tracked-questions-panel
            title="Questions we're tracking for you"
            subtitle="Real questions customers ask AI in your space"
            :questions="overview.questions"
            :action-to="questionsTabRoute"
            @view-all="navigateToDetailTab('AiVisibilityQuestions')"
        />
        <citation-sources-panel
            title="Where AI finds information about you"
            subtitle="The websites AI cites when answering these questions"
            :citations="overview.citationSources"
            :action-to="citationsTabRoute"
            @view-all="navigateToDetailTab('AiVisibilityCitations')"
        />
      </div>

      <recommendation-list
          title="What we recommend you do next"
          subtitle="Three steps we've picked for you this week - ordered by impact"
          :items="overview.recommendations"
          :site-id="siteId"
          :tracker-id="trackerId"
          :citations-action-to="citationsTabRoute"
          @navigate-tab="navigateToDetailTab"
          v-if="overview.recommendations.length > 0"
      />
    </template>
  </section>
</template>

<script>
import {getAiVisibilityOverview} from '../../../api'
import LoadingState from '../../ui/states/LoadingState.vue'
import ErrorState from '../../ui/states/ErrorState.vue'
import EmptyState from '../../ui/states/EmptyState.vue'
import MetricGrid from '../../ui/cards/MetricGrid.vue'
import MetricCard from '../../ui/cards/MetricCard.vue'
import ScoreMetricCard from '../../ui/cards/ScoreMetricCard.vue'
import VisibilityTrendChart from './components/VisibilityTrendChart.vue'
import CompetitorScorePanel from './components/CompetitorScorePanel.vue'
import TrackedQuestionsPanel from './components/TrackedQuestionsPanel.vue'
import CitationSourcesPanel from './components/CitationSourcesPanel.vue'
import RecommendationList from './components/RecommendationList.vue'

function createEmptyOverview() {
  return {
    run: null,
    metrics: null,
    scoreHistory: [],
    competitors: {total: 0, rows: []},
    questions: {total: 0, rows: []},
    citationSources: {total: 0, rows: []}
  }
}

export default {
  name: 'AiVisibilityOverviewTab',
  components: {
    LoadingState,
    ErrorState,
    EmptyState,
    MetricGrid,
    MetricCard,
    ScoreMetricCard,
    VisibilityTrendChart,
    CompetitorScorePanel,
    TrackedQuestionsPanel,
    CitationSourcesPanel,
    RecommendationList,
  },
  props: {
    siteId: {
      type: [Number, String],
      required: true,
    },
    trackerId: {
      type: [Number, String],
      required: true,
    },
    detail: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    filterOptions: {
      type: Object,
      default() {
        return {}
      },
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      loadError: '',
      overview: createEmptyOverview(),
    }
  },
  computed: {
    visibilityScoreMetric() {
      return this.overview?.metrics?.visibilityScore;
    },
    mentionRateMetic() {
      return this.overview?.metrics?.mentionRate;
    },
    shareOfConversationMetic() {
      return this.overview?.metrics?.shareOfConversation;
    },
    linkedToSiteCountMetic() {
      return this.overview?.metrics?.linkedToSiteCount;
    },
    overviewMetricTrends() {
      return this.overview?.metrics?.trends || {}
    },
    visibilityScoreTrend() {
      return this.buildTrend(this.overviewMetricTrends.visibilityScore, ' points')
    },
    mentionRateTrend() {
      return this.buildTrend(this.overviewMetricTrends.mentionRate, '%')
    },
    shareOfConversationTrend() {
      return this.buildTrend(this.overviewMetricTrends.shareOfConversation, '%')
    },
    linkedToSiteCountTrend() {
      return this.buildTrend(this.overviewMetricTrends.linkedToSiteCount)
    },
    competitorsTabRoute() {
      return this.getDetailTabRoute('AiVisibilityCompetitors')
    },
    questionsTabRoute() {
      return this.getDetailTabRoute('AiVisibilityQuestions')
    },
    citationsTabRoute() {
      return this.getDetailTabRoute('AiVisibilityCitations')
    },
    visibilityScoreHistory() {
      const result = {
        title: 'How your visibility has changed',
        subtitle: 'Snapshots for the selected date range',
        infoTitle: 'Shows your AI Visibility Score over time for the selected date range. Each point is one snapshot of how visible your brand was in AI answers that day. The score is based on how often AI mentioned you, where you ranked, your share of brand mentions, and how often AI linked to your site. Higher is better.',
        thresholdLabel: '"Good" threshold',
        categories: [],
        points: [],
      }

      this.overview?.scoreHistory?.forEach(value => {
        const finishedAt = new Date(value.finishedAt);
        const formatter = new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric'
        });

        result.categories.push(formatter.format(finishedAt));
        result.points.push(value.visibilityScore);
      })

      return result
    },
  },
  watch: {
    siteId: 'loadOverview',
    trackerId: 'loadOverview',
    'filters.range': 'loadOverview',
    'filters.model': 'loadOverview',
  },
  beforeMount() {
    this.loadOverview()
  },
  methods: {
    buildTrend(value, unit = '') {
      if (value === null) return null;

      const numericValue = Number(value || 0)

      if (!numericValue) {
        return {
          direction: 'flat',
          tone: 'neutral',
          label: 'No change',
        }
      }

      const formattedValue = `${numericValue > 0 ? '+' : ''}${numericValue}`
      return {
        direction: numericValue > 0 ? 'up' : 'down',
        tone: numericValue > 0 ? 'positive' : 'negative',
        label: `${formattedValue}${unit ? `${unit}` : ''} this week`,
      }
    },
    getDetailTabRoute(name) {
      if (this.embedded) {
        return null
      }
      return {
        name,
        params: {
          siteId: String(this.siteId),
          trackerId: String(this.trackerId),
        },
      }
    },
    navigateToDetailTab(name) {
      if (!this.embedded) {
        return
      }
      this.$emit('navigate-tab', {name})
    },
    loadOverview() {
      this.loading = true
      this.loadError = ''

      return getAiVisibilityOverview(this.siteId, this.trackerId, this.filters)
          .then((overview) => {
            this.overview = overview.data
          })
          .catch(() => {
            this.overview = createEmptyOverview()
            this.loadError = 'Could not load AI Visibility overview data.'
          })
          .finally(() => {
            this.loading = false
          })
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-overview-tab {
  margin-top: 24px;
  padding-bottom: 30px;
}

.ai-visibility-overview-tab__section-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 32px;
}

@media (max-width: 1080px) {
  .ai-visibility-overview-tab__section-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 640px) {
  .ai-visibility-overview-tab {
    margin-top: 18px;
  }
}
</style>
